/* eslint-disable import/no-anonymous-default-export */
// © Microsoft Corporation. All rights reserved.
import React from 'react';
import { Stack, Dropdown, IDropdownOption } from '@fluentui/react';
import { VideoDeviceInfo, AudioDeviceInfo, DeviceManager, LocalVideoStream } from '@azure/communication-calling';
import { localSettingsContainer, dropDownStyles, micStackTokens, mainStackTokens } from './styles/LocalSettings.styles';
import { labelConstants, URLOptionsConstants } from '../core/constants';
import { IsHiddenMode, IsProctorMode, IsSecondaryStream, IsProctorCameraEnabled } from '../Utils/Utils';
import { GetSelectedMicrophone, SetSelectedMicrophone, SetSelectedCamera, GetSelectedCamera } from '../core/sideEffects';
import { IRemoteLocalSettingsProps } from './RemoteLocalSettings';

let selectedCameraId: string = '';
let selectedMicrophoneId: string = '';
let localSettingsProps: LocalSettingsProps;

export interface LocalSettingsProps {
    deviceManager: DeviceManager;
    videoDeviceList: VideoDeviceInfo[];
    audioDeviceList: AudioDeviceInfo[];
    videoDeviceInfo: VideoDeviceInfo;
    audioDeviceInfo: AudioDeviceInfo;
    setVideoDeviceInfo(device: VideoDeviceInfo): void;
    setAudioDeviceInfo(device: AudioDeviceInfo): void;
    setLocalVideoStream(localVideoStream: LocalVideoStream | undefined): void;
}

export const UserLocalSettings = (): IRemoteLocalSettingsProps => {

    let selectedMicrophoneNum: number = -1;
    let selectedCameraNum: number = -1;

    const urlParams = new URLSearchParams(window.location.search.substring(1));
    const selectedMicrophone = urlParams.get(URLOptionsConstants.MICROPHONEINDEX);
    const selectedCamera = urlParams.get(URLOptionsConstants.CAMERAINDEX);
    const userId = urlParams.get(URLOptionsConstants.USERID);

    if (selectedCamera)
        selectedCameraNum = parseInt(selectedCamera);
    if (selectedMicrophone)
        selectedMicrophoneNum = parseInt(selectedMicrophone);

    const settings: IRemoteLocalSettingsProps = {
        videoDeviceList: localSettingsProps?.videoDeviceList?.map((a) => a.name),
        audioDeviceList: localSettingsProps?.audioDeviceList?.map((a) => a.name),
        videoDevice: localSettingsProps?.videoDeviceInfo?.name,
        audioDevice: localSettingsProps?.audioDeviceInfo?.name,
        videoIndex: selectedCameraNum,
        audioIndex: selectedMicrophoneNum,
        userId: (userId ? userId : '')
    };

    return settings;
}

export default (props: LocalSettingsProps): JSX.Element => {
    localSettingsProps = { ...props };

    const defaultPlaceHolder = labelConstants.DefaultPlaceHolder;

    const getVideoDropDownList = (list: Array<VideoDeviceInfo>): IDropdownOption[] => {
        if (list.length > 0) {
            if ( GetSelectedCamera()<0) {
                selectedCameraId = list[0].id;
                SetSelectedCamera(0);
            }
            else {
                let i: number = 0;
                list.forEach((data) => {
                    if (i === GetSelectedCamera()) {
                        selectedCameraId = data.id;
                        let camindex = list.indexOf(data);
                        SetSelectedCamera(camindex);
                    }
                    i++;
                });
            }
        }

        list = list.sort((a, b) =>
            a.name > b.name ? 1 : -1,
        );

        return list.map((item) => ({
            val: item,
            key: item.id,
            text: item.name === '' ? item.deviceType : item.name
        }));
    };

    const getAudioDropDownList = (list: Array<AudioDeviceInfo>): IDropdownOption[] => {
        if (list.length > 0) {
            if (GetSelectedMicrophone()<0) {
                selectedMicrophoneId = list[0].id;

                if (!IsSecondaryStream())
                    SetSelectedMicrophone(0);
            }
            else {
                let i: number = 0;
                list.forEach((data) => {
                    if (i === GetSelectedMicrophone()) {
                        selectedMicrophoneId = data.id;
                        let micindex = list.indexOf(data);

                        if (!IsSecondaryStream())
                            SetSelectedMicrophone(micindex);
                    }
                    i++;
                });
            }
        }

        return list.map((item) => ({
            val: item,
            key: item.id,
            text: item.name === '' ? item.deviceType : item.name
        }));
    };



    return (
        <Stack className={localSettingsContainer} tokens={mainStackTokens} style={{display: IsSecondaryStream() ? 'none' : '' }} >
            {(!(IsProctorMode() && !IsProctorCameraEnabled())) && (
                <Dropdown
                    id={"drpVideo"}
                    placeholder={defaultPlaceHolder}
                    hidden={IsSecondaryStream()}
                    label={labelConstants.Camera}
                    options={getVideoDropDownList(props.videoDeviceList)}
                    styles={dropDownStyles}
                    disabled={props.videoDeviceList.length === 0 || IsHiddenMode() || IsSecondaryStream()}
                    defaultSelectedKey={
                        GetSelectedCamera() > -1 ? selectedCameraId : (props.videoDeviceInfo ? props.videoDeviceInfo.id : '')}
                    onChange={(...args): void => {
                        const index = args[2] ?? 0;
                        SetSelectedCamera(index);

                        if (args[1]) {
                            props.videoDeviceList.forEach((data) => {
                                if (data.name === args[1]?.text.toString())
                                {
                                    props.setVideoDeviceInfo(data);
                                    const localVideoStream = new LocalVideoStream(data as VideoDeviceInfo);
                                    props.setLocalVideoStream(localVideoStream);
                                }
                            });

                            localSettingsProps = { ...props };
                        }
                    }}
                />
            )}
            <Stack tokens={micStackTokens} style={{display: IsSecondaryStream() ? 'none' : '' }}>
                <Dropdown
                    id={"drpAudio"}
                    placeholder={defaultPlaceHolder}
                     hidden={IsSecondaryStream()}
                    label={labelConstants.Microphone}
                    styles={dropDownStyles}
                    disabled={props.audioDeviceList.length === 0 || IsHiddenMode()}
                    options={getAudioDropDownList(props.audioDeviceList)}
                    defaultSelectedKey={
                        GetSelectedMicrophone() > -1 ? selectedMicrophoneId : (props.audioDeviceInfo ? props.audioDeviceInfo.id : '')}
                    onChange={(...args): void => {
                        const index = args[2] ?? 0;
                        SetSelectedMicrophone(index);
                        props.setAudioDeviceInfo(props.audioDeviceList[index]);
                        props.deviceManager.selectMicrophone(props.audioDeviceList[index]);
                        localSettingsProps = { ...props };
                    }}
                />
            </Stack>
        </Stack>
    );
};